import { useState } from 'react';
import { createLogger } from '../utils/logging';
import equal from 'deep-equal';
import { isEqualToEmptyValue } from '../utils';

const logger = createLogger('useExtendedState');

export const NO_STATE = {
    value: undefined,
    timestamp: undefined,
    setValue: () => {
        logger.warn('Calling "setNewValue()" method on NO_STATE object!');
    },
    isChanged: undefined,
    reset: () => {
        logger.warn('Calling "reset()" method on NO_STATE object!');
    },
    changeInitialState: () => {
        logger.warn('Calling "changeInitialState()" method on NO_STATE object!');
    },
};

export function useExtendedState(initialState, rerenderWhenUpdated = true) {
    const [value, setValue] = useState(new ExtendedState(initialState));
    if (rerenderWhenUpdated) {
        value.valueSetter = (newValue) => {
            setValue(new ExtendedState(value.initialValue, newValue));
        };
    }
    return value;
}

export function ExtendedState(initialState, value) {
    const effectiveValue = value !== undefined ? value: initialState;

    this.initialValue = initialState;
    this.value = effectiveValue;
    this.isChanged = isChanged(this.initialValue, this.value);
    this.valueSetter = null; // expected to be initialized by the caller

    this.setValue = (newValue) => {
        if (this.valueSetter) {
            this.valueSetter(newValue);
        } else {
            this.value = newValue;
            this.isChanged = isChanged(this.initialValue, this.value);
        }
    };
    this.reset = () => {
        this.setValue(this.initialValue);
    };
    this.setValueIfNew = (newValue) => {
        if (isChanged(this.value, newValue)) {
            this.setValue(newValue);
        }
    };
    this.resetIfNecessary = () => {
        if (isChanged(this.initialValue, this.value)) {
            this.reset();
        }
    };
    this.changeInitialState = (newInitialState) => {
        this.initialValue = newInitialState;
        this.isChanged = isChanged(this.initialValue, this.value);
    };
}

function isChanged(initialValue, currentValue) {
    return !isEqualToEmptyValue(initialValue, currentValue) && !equal(initialValue, currentValue);
}

export default useExtendedState;
